import { useState, useMemo } from 'react';
import styled from 'styled-components';
import { Image } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ImageLightbox from './ImageLightbox';

export default function GalleryView({ messages, galleryArtworks = [], circleId, onDrawOnThis, onEditWithRoss, onStyleTransfer }) {
  const { currentUser } = useAuth();
  const [filter, setFilter] = useState('all');
  const [selectedIndex, setSelectedIndex] = useState(null);

  const imageMessages = useMemo(() => {
    const fromGallery = galleryArtworks.length > 0;
    const raw = fromGallery
      ? galleryArtworks.map((a) => ({
          id: a.id,
          imageUrl: a.imageUrl,
          fullResImageUrl: a.fullResImageUrl || a.imageUrl,
          displayName: a.createdByDisplayName,
          userId: a.createdByUserId,
          timestamp: a.timestamp || a.savedAt?.toMillis?.() || 0,
        }))
      : messages
          .filter((m) => m.imageUrl)
          .map((m) => ({
            id: m.id,
            imageUrl: m.imageUrl,
            fullResImageUrl: m.fullResImageUrl || m.imageUrl,
            displayName: m.displayName,
            userId: m.userId,
            timestamp: m.timestamp || 0,
          }));

    const sorted = [...raw].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));

    if (filter === 'mine') {
      return sorted.filter((m) => m.userId === currentUser?.uid);
    }
    return sorted;
  }, [messages, galleryArtworks, filter, currentUser]);

  if (imageMessages.length === 0) {
    return (
      <EmptyGallery>
        <EmptyIcon>
          <Image size={48} />
        </EmptyIcon>
        <EmptyText>
          {filter === 'mine' ? 'You haven\'t created any art yet.' : 'No artwork in this circle yet.'}
        </EmptyText>
      </EmptyGallery>
    );
  }

  return (
    <>
      <FilterBar>
        <FilterBtn $active={filter === 'all'} onClick={() => setFilter('all')}>All</FilterBtn>
        <FilterBtn $active={filter === 'mine'} onClick={() => setFilter('mine')}>Mine</FilterBtn>
      </FilterBar>

      <Grid>
        {imageMessages.map((msg, idx) => (
          <Thumbnail key={msg.id || idx} onClick={() => setSelectedIndex(idx)}>
            <ThumbnailImg src={msg.imageUrl} alt="" loading="lazy" />
            {msg.displayName && <AuthorBadge>{msg.displayName}</AuthorBadge>}
          </Thumbnail>
        ))}
      </Grid>

      {selectedIndex !== null && (
        <ImageLightbox
          images={imageMessages}
          initialIndex={selectedIndex}
          onClose={() => setSelectedIndex(null)}
          onDrawOnThis={onDrawOnThis}
          onEditWithRoss={onEditWithRoss}
          onStyleTransfer={onStyleTransfer}
        />
      )}
    </>
  );
}

const FilterBar = styled.div`
  display: flex;
  gap: 8px;
  padding: 12px 16px 8px;
  flex-shrink: 0;
`;

const FilterBtn = styled.button`
  padding: 8px 16px;
  border-radius: ${({ theme }) => theme.radius.full};
  font-size: 15px;
  font-weight: 600;
  background: ${({ $active, theme }) => ($active ? theme.colors.accent : theme.colors.surface)};
  color: ${({ $active, theme }) => ($active ? '#fff' : theme.colors.textSecondary)};
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.accent : theme.colors.border)};
  transition: all 0.15s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  padding: 12px 12px 24px;
  overflow-y: auto;
  flex: 1;
  align-content: start;
`;

const Thumbnail = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 100%;
  overflow: hidden;
  border-radius: 10px;
  cursor: pointer;
  background: ${({ theme }) => theme.colors.surface || '#f9fafb'};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const ThumbnailImg = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const AuthorBadge = styled.span`
  position: absolute;
  bottom: 6px;
  left: 6px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-radius: 9999px;
  padding: 3px 8px;
  font-size: 12px;
  font-weight: 700;
  color: white;
`;

const EmptyGallery = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 40px;
  text-align: center;
`;

const EmptyIcon = styled.div`
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const EmptyText = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.6;
`;
